/**
 * Rich page-view tracking for the Observ RUM SDK (Kapitalvins parity).
 *
 * Emits an `app.page_view` OTel log event on:
 *   - the initial document load (`navigation_type=load`, with the entry `url.path`
 *     and the document `referrer` when present), and
 *   - each SPA route change (`navigation_type=spa`), carrying the previous path
 *     (`referrer_path`) and the time spent on it (`time_on_previous_ms`).
 *
 * This is the richer analytics counterpart to the lightweight
 * `observ.session.navigate` marker emitted by the semantic-event tap (both ride
 * the shared `history` observer, so `history` is patched once). `url.path` is
 * captured WITHOUT query string or hash, so no PII leaks through it. A navigation
 * counts as activity → the session is `touch()`-ed.
 *
 * Best-effort and never throws; degrades to a no-op without a DOM / logs channel.
 */
import type { Logger } from '@opentelemetry/api-logs'

import { onHistoryNavigation } from './history-nav.js'
import { emitSessionEvent, setupOtelLogs } from './otel-logs.js'
import { touchSession } from './session.js'
import type { ObservInitOptions } from './types.js'

/** Canonical `event.name` for a page view. */
export const PAGE_VIEW_EVENT = 'app.page_view'

/** Public handle for the running page-view tracker. */
export interface PageViewsHandle {
  /** Unsubscribe from history navigations. Idempotent. */
  stop(): void
}

const NOOP: PageViewsHandle = { stop: () => {} }

/** Module-level guard so the tracker is self-idempotent (one per page). */
let active: PageViewsHandle | null = null

/** Strip query string + hash so `url.path` never carries PII. */
export function sanitizePath(path: string): string {
  return (path.split('?')[0] ?? path).split('#')[0] ?? ''
}

/** High-resolution-ish clock for time-on-page, falling back to epoch ms. */
function nowMs(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
}

/**
 * Start emitting `app.page_view` events. Returns a handle whose `stop()`
 * unsubscribes. Emits the initial `load` view synchronously, then one `spa` view
 * per path change. NOT gated by `disableReplay` — page views are useful without
 * replay.
 */
export function startPageViews(options: ObservInitOptions): PageViewsHandle {
  if (active) return active
  if (typeof window === 'undefined' || typeof document === 'undefined') return NOOP

  let logger: Logger
  try {
    logger = setupOtelLogs(options)
  } catch {
    return NOOP
  }

  let currentPath = sanitizePath(typeof location !== 'undefined' ? location.pathname : '/')
  let enteredAt = nowMs()

  // Initial load view (alongside the auto-instrumented documentLoad span).
  try {
    const loadAttrs: Record<string, string> = {
      'url.path': currentPath,
      'app.page_view.navigation_type': 'load',
    }
    if (document.referrer) loadAttrs['app.page_view.referrer'] = document.referrer
    emitSessionEvent(logger, PAGE_VIEW_EVENT, loadAttrs)
  } catch {
    /* swallow — a failed initial view must never break init */
  }

  const unsubscribe = onHistoryNavigation(() => {
    try {
      const toPath = sanitizePath(typeof location !== 'undefined' ? location.pathname : currentPath)
      touchSession() // a navigation is activity
      // Only a real path change is a page view — skip query-only replaceState and
      // duplicate events so analytics stay clean.
      if (toPath === currentPath) return
      const now = nowMs()
      emitSessionEvent(logger, PAGE_VIEW_EVENT, {
        'url.path': toPath,
        'app.page_view.referrer_path': currentPath,
        'app.page_view.time_on_previous_ms': String(Math.round(now - enteredAt)),
        'app.page_view.navigation_type': 'spa',
      })
      currentPath = toPath
      enteredAt = now
    } catch {
      /* never let a navigation handler break the host page */
    }
  })

  let stopped = false
  const handle: PageViewsHandle = {
    stop(): void {
      if (stopped) return
      stopped = true
      active = null
      unsubscribe()
    },
  }
  active = handle
  return handle
}
